import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Task as TaskIcon,
  Email as EmailIcon,
  Flag as FlagIcon,
  AttachFile as AttachmentIcon,
} from '@mui/icons-material';
import type { Email } from '../../stores/mailStore';

interface EmailToTaskDialogProps {
  open: boolean;
  onClose: () => void;
  email: Email | null;
  onCreateTask: (task: TaskData) => void;
}

interface TaskData {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  dueDate: string;
  emailReference?: string;
  includeEmailBody: boolean;
  includeAttachments: boolean;
}

const EmailToTaskDialog: React.FC<EmailToTaskDialogProps> = ({
  open,
  onClose,
  email,
  onCreateTask,
}) => {
  const [taskData, setTaskData] = useState<TaskData>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0], // Tomorrow
    includeEmailBody: true,
    includeAttachments: true,
  });

  React.useEffect(() => {
    if (email) {
      const fromAddress = typeof email.from === 'string' 
        ? email.from 
        : email.from?.address || 'Unknown';
      
      setTaskData({
        title: `Follow up: ${email.subject}`,
        description: `From: ${fromAddress}\n\n${email.body ? email.body.substring(0, 500) + '...' : ''}`,
        priority: email.isImportant ? 'high' : 'medium',
        dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        emailReference: email.id,
        includeEmailBody: true,
        includeAttachments: email.attachments?.length > 0,
      });
    }
  }, [email]);

  const handleCreate = () => {
    onCreateTask(taskData);
    onClose();
    // Reset form
    setTaskData({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      includeEmailBody: true,
      includeAttachments: true,
    });
  };

  if (!email) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TaskIcon color="primary" />
        Create Task from Email
      </DialogTitle>
      <DialogContent>
        {/* Email Reference */}
        <Box sx={{ 
          bgcolor: 'action.hover', 
          p: 2, 
          borderRadius: 1, 
          mb: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}>
          <EmailIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {email.subject}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              From: {typeof email.from === 'string' ? email.from : email.from?.address}
            </Typography>
            {email.attachments?.length > 0 && (
              <Chip
                size="small"
                icon={<AttachmentIcon />}
                label={`${email.attachments.length} attachment${email.attachments.length > 1 ? 's' : ''}`}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Box>

        {/* Task Title */}
        <TextField
          fullWidth
          label="Task Title"
          value={taskData.title}
          onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
          sx={{ mb: 2 }}
          placeholder="Enter task title..."
        />

        {/* Task Description */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description"
          value={taskData.description}
          onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
          sx={{ mb: 2 }}
          placeholder="Add task description..."
        />

        {/* Priority & Due Date Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={taskData.priority}
              label="Priority"
              onChange={(e) => setTaskData({ ...taskData, priority: e.target.value as any })}
            >
              <MenuItem value="low">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagIcon sx={{ color: 'text.secondary' }} />
                  Low
                </Box>
              </MenuItem>
              <MenuItem value="medium">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagIcon sx={{ color: 'warning.main' }} />
                  Medium
                </Box>
              </MenuItem>
              <MenuItem value="high">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagIcon sx={{ color: 'error.main' }} />
                  High
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="date"
            label="Due Date"
            value={taskData.dueDate}
            onChange={(e) => setTaskData({ ...taskData, dueDate: e.target.value })}
            sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Options */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={taskData.includeEmailBody}
                onChange={(e) => setTaskData({ ...taskData, includeEmailBody: e.target.checked })}
              />
            }
            label="Include email content in task"
          />
          {email.attachments?.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskData.includeAttachments}
                  onChange={(e) => setTaskData({ ...taskData, includeAttachments: e.target.checked })}
                />
              }
              label="Link attachments to task"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleCreate}
          disabled={!taskData.title.trim()}
          startIcon={<TaskIcon />}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailToTaskDialog;
